import {
  RtcConfig,
  RtcIceOutcome,
  RtcSignal,
  RtcSignalType,
  sameSubject,
} from './direct-call-rtc';
import {
  defaultSetTimer,
  ICE_STATS_RETRY_MS,
  MAX_EARLY_CANDIDATES,
  MAX_PRESTART_SIGNALS,
  RtcDescriptionLike,
  RtcLocalMedia,
  RtcMediaState,
  RtcMeshOptions,
  RtcPeerLike,
  RtcSenderLike,
  RtcTrackLike,
  sdpSession,
  selectedCandidateType,
  STALE_OFFER_MS,
} from './direct-call-rtc-mesh-types';

export * from './direct-call-rtc-mesh-types';

interface PeerEntry {
  pc: RtcPeerLike;
  polite: boolean;
  makingOffer: boolean;
  ignoreOffer: boolean;
  /** True on the side that sent the pair's first offer: it alone reports the ICE outcome. */
  offered: boolean;
  reported: boolean;
  /** An outcome read or POST is in flight; a second state change waits for it rather than racing it. */
  reporting: boolean;
  /** Set once the remote description is applied; candidates before that wait in `pending`. */
  remoteSet: boolean;
  pending: unknown[];
  tracksAdded: boolean;
  /** When this side sent the pair's first offer, while no answer has come back. */
  offerSentAt: number | null;
  /**
   * The latest offer the impolite side ignored while its own first offer was unanswered, and the cancel of the
   * timer that re-runs it once that offer is stale. Without it, a human who arrives inside the window is never
   * answered: nothing else re-checks the pair (7574 review, thread 102284).
   */
  heldOffer: RtcDescriptionLike | null;
  cancelRetry: (() => void) | null;
  /** SDP session ids of every description accepted from this peer: its connection's identity (S6c-1). */
  sessions: Set<string>;
  /** The sender carrying camera or screen; kept so a share swaps it even after its track went null. */
  video: RtcSenderLike | null;
}

/** A call has a mesh cap: the caller joins audio-only when video would put it over the video cap. */
export function joinsWithVideo(config: RtcConfig): boolean {
  return config.peers.length + 1 <= config.maxVideoHumans;
}

/**
 * The wire form of a description. A browser's RTCSessionDescription keeps `type` and `sdp` as prototype
 * accessors, so spreading it copies nothing; read the two fields by name (7438 review, thread 100561).
 */
const descriptionPayload = (d: RtcDescriptionLike) => ({
  type: d.type,
  sdp: d.sdp,
});

/**
 * US 37334 (Feature 37328 S6): one RTCPeerConnection per other human in the call (a mesh), negotiated
 * with the "perfect negotiation" pattern so both sides may offer at once without deadlock. The
 * peer-connection shape is structural so this runs on web, in a hosted web view and under node tests.
 */
export class RtcMesh {
  private readonly peers = new Map<string, PeerEntry>();
  /**
   * Candidates from a human we hold no connection to yet, by lower-cased subject. The two HTTP posts are
   * unordered, so a candidate gathered while the offer was being set often arrives before the offer.
   */
  private readonly early = new Map<string, unknown[]>();
  /** Per peer, the signal being handled: signals from one human are applied in arrival order. */
  private readonly chains = new Map<string, Promise<void>>();
  private config: RtcConfig | null = null;
  private local: RtcLocalMedia | null = null;
  private screen: RtcTrackLike | null = null;
  private videoAllowed = false;
  /** Until the first start(), this call's signals wait in `prestart` (a member still opening its camera). */
  private started = false;
  private readonly prestart: RtcSignal[] = [];
  /**
   * The SDP sessions of each human's connections that hung up, by lower-cased subject: a late offer from one builds
   * nothing, while a reload, on a new connection with a new session id, connects at once (S6c-1).
   */
  private readonly left = new Map<string, Set<string>>();

  constructor(private readonly options: RtcMeshOptions) {}

  /** The other humans this mesh holds a connection to. */
  get connected(): string[] {
    return [...this.peers.keys()];
  }

  /** Join: connect to every human already in the call. A newcomer offers; members answer. */
  start(config: RtcConfig, local: RtcLocalMedia): void {
    if (config.peers.length + 1 > config.maxAudioHumans)
      throw new Error('This call is full');
    this.config = config;
    this.videoAllowed = joinsWithVideo(config);
    this.local = this.videoAllowed ? local : { ...local, video: undefined };
    this.started = true;
    for (const peer of config.peers) this.addTracks(peer, this.ensure(peer));
    // A newcomer that offered while this member was still opening its camera is answered now, in arrival order.
    for (const signal of this.prestart.splice(0))
      void this.receive(signal).catch(() => undefined);
  }

  async receive(signal: RtcSignal): Promise<void> {
    if (!this.started) {
      if (
        signal.conversationId === this.options.conversationId &&
        this.prestart.length < MAX_PRESTART_SIGNALS
      )
        this.prestart.push(signal);
      return;
    }
    if (
      !this.config ||
      signal.conversationId !== this.options.conversationId ||
      sameSubject(signal.from, this.config.self)
    )
      return;
    const peer = this.key(signal.from);
    return this.enqueue(peer, () => this.handle(peer, signal));
  }

  /** Runs `work` after every signal already queued for this peer, so one human's signals apply in order. */
  private enqueue(peer: string, work: () => Promise<void>): Promise<void> {
    const run = (this.chains.get(peer) ?? Promise.resolve()).then(work);
    const tail = run.then(
      () => undefined,
      () => undefined
    );
    this.chains.set(peer, tail);
    void tail.then(() => {
      if (this.chains.get(peer) === tail) this.chains.delete(peer);
    });
    return run;
  }

  private async handle(peer: string, signal: RtcSignal): Promise<void> {
    if (!this.config) return;
    if (signal.type === 'hangup') {
      const gone = this.left.get(peer.toLowerCase()) ?? new Set<string>();
      for (const s of this.peers.get(peer)?.sessions ?? []) gone.add(s);
      this.left.set(peer.toLowerCase(), gone);
      return this.drop(peer, true);
    }
    if (signal.type === 'media-state') {
      if (!this.peers.has(peer)) return;
      const p = signal.payload ?? {};
      this.options.onMediaState?.(peer, {
        audio: p['audio'] === true,
        video: p['video'] === true,
        screen: p['screen'] === true,
      });
      return;
    }
    if (signal.type === 'ice') {
      const candidate = signal.payload?.['candidate'] ?? null;
      const entry = this.peers.get(peer);
      if (!entry) return this.holdEarly(peer, candidate);
      if (!entry.remoteSet) {
        if (entry.pending.length < MAX_EARLY_CANDIDATES)
          entry.pending.push(candidate);
        return;
      }
      try {
        await entry.pc.addIceCandidate(candidate);
      } catch (err) {
        if (!entry.ignoreOffer) throw err;
      }
      return;
    }
    // An offer from a human we have not met is a newcomer: the bridge already checked both are in the call.
    // A newcomer past the audio cap (us, the peers we hold, and them) gets no connection.
    const sdp =
      typeof signal.payload?.['sdp'] === 'string'
        ? signal.payload['sdp']
        : undefined;
    const session = sdpSession(sdp);
    if (
      !this.peers.has(peer) &&
      (signal.type === 'answer' ||
        this.peers.size + 2 > this.config.maxAudioHumans ||
        (session !== undefined &&
          !!this.left.get(peer.toLowerCase())?.has(session)))
    )
      return;
    await this.describe(peer, this.ensure(peer), { type: signal.type, sdp });
  }

  /** Send a screen in place of the camera to every peer that receives video. */
  async shareScreen(track: RtcTrackLike): Promise<void> {
    this.screen = track;
    await this.replaceVideo(track);
  }

  /** Stop sharing: the camera returns, or no video when there was none. */
  async stopShare(): Promise<void> {
    this.screen = null;
    await this.replaceVideo(this.local?.video ?? null);
  }

  async setMediaState(state: RtcMediaState): Promise<void> {
    await Promise.all(
      [...this.peers.keys()].map((peer) =>
        this.options.send(peer, 'media-state', { ...state })
      )
    );
  }

  /**
   * Leave: close every connection and stop taking signals at once, then tell every peer. Nothing awaits before
   * the close, so an offer that lands while the hangup POSTs are in flight cannot build a connection that
   * would keep sending the user's media after they pressed hang up (7438 review, thread 100785).
   */
  async hangup(): Promise<void> {
    const peers = [...this.peers.keys()];
    this.config = null;
    for (const peer of peers) this.drop(peer, false);
    this.early.clear();
    this.prestart.length = 0;
    this.left.clear();
    // A later start() on this mesh must offer the camera, never a screen track from the last call (7438, thread 100934).
    this.screen = null;
    this.local = null;
    this.videoAllowed = false;
    await Promise.allSettled(
      peers.map((p) => this.options.send(p, 'hangup', null))
    );
  }

  private key(subject: string): string {
    for (const k of this.peers.keys()) if (sameSubject(k, subject)) return k;
    return subject;
  }

  private ensure(peer: string): PeerEntry {
    const existing = this.peers.get(peer);
    if (existing) return existing;
    if (!this.config) throw new Error('Call not started');
    const pc = this.options.createPeer(this.config.iceServers);
    const entry: PeerEntry = {
      pc,
      // Both sides compute the same answer from the same two subjects, so exactly one is polite.
      polite: this.config.self.toLowerCase() > peer.toLowerCase(),
      makingOffer: false,
      ignoreOffer: false,
      offered: false,
      reported: false,
      reporting: false,
      remoteSet: false,
      offerSentAt: null,
      heldOffer: null,
      cancelRetry: null,
      sessions: new Set<string>(),
      pending: this.early.get(peer.toLowerCase()) ?? [],
      tracksAdded: false,
      video: null,
    };
    this.early.delete(peer.toLowerCase());
    // Fire-and-forget: a failed post (an aborted fetch during hangup) must not become an unhandled rejection.
    pc.onnegotiationneeded = () =>
      void this.offer(peer, entry).catch(() => undefined);
    pc.onicecandidate = ({ candidate }) => {
      if (candidate)
        void this.options
          .send(peer, 'ice', { candidate })
          .catch(() => undefined);
    };
    pc.ontrack = ({ track, streams }) =>
      this.options.onRemoteTrack(peer, track, streams);
    pc.oniceconnectionstatechange = () => void this.reportIce(peer, entry);
    this.peers.set(peer, entry);
    return entry;
  }

  private addTracks(_peer: string, entry: PeerEntry): void {
    if (entry.tracksAdded || !this.local) return;
    entry.tracksAdded = true;
    if (this.local.audio)
      entry.pc.addTrack(this.local.audio, this.local.stream);
    const video = this.screen ?? this.local.video;
    if (video && this.videoAllowed)
      entry.video = entry.pc.addTrack(video, this.local.stream);
    // With nothing to send, nothing triggers negotiation: a receive-only audio transceiver makes the newcomer
    // offer. A member answering already has the offer's transceivers, so it adds none (S6c-1).
    else if (!this.local.audio && !entry.remoteSet)
      entry.pc.addTransceiver('audio', { direction: 'recvonly' });
  }

  private async offer(peer: string, entry: PeerEntry): Promise<void> {
    try {
      entry.makingOffer = true;
      await entry.pc.setLocalDescription();
      const description = entry.pc.localDescription;
      if (!description || !this.isLive(peer, entry)) return;
      // Only the pair's first negotiation decides who reports: a later renegotiation (a screen share
      // adding a sender) by the side that answered must not make it a second reporter.
      entry.offered =
        entry.offered || (description.type === 'offer' && !entry.remoteSet);
      await this.options.send(
        peer,
        description.type as RtcSignalType,
        descriptionPayload(description)
      );
      if (description.type === 'offer' && !entry.remoteSet)
        entry.offerSentAt ??= this.clock();
    } finally {
      entry.makingOffer = false;
    }
  }

  private async describe(
    peer: string,
    entry: PeerEntry,
    description: RtcDescriptionLike
  ): Promise<void> {
    // An answer we are not waiting for (a duplicate, or a late one after a rollback) is dropped, not an error.
    if (description.type === 'answer' && entry.pc.signalingState === 'stable')
      return;
    const collision =
      description.type === 'offer' &&
      (entry.makingOffer || entry.pc.signalingState !== 'stable');
    entry.ignoreOffer = !entry.polite && collision && !this.staleOffer(entry);
    if (entry.ignoreOffer) {
      if (entry.offerSentAt !== null) this.holdOffer(peer, entry, description);
      return;
    }
    const firstNegotiation = !entry.remoteSet;
    await entry.pc.setRemoteDescription(description);
    if (!this.isLive(peer, entry)) return;
    entry.remoteSet = true;
    entry.offerSentAt = null;
    this.clearHeld(entry);
    const session = sdpSession(description.sdp);
    if (session !== undefined) entry.sessions.add(session);
    await this.flushPending(entry);
    if (description.type !== 'offer' || !this.isLive(peer, entry)) return;
    // An offer accepted during the pair's first negotiation means ours, if any, lost glare: the other side
    // reports. A later renegotiation offer leaves the reporter as it is.
    if (firstNegotiation) entry.offered = false;
    // A member answering a newcomer adds its media now, onto the transceivers the offer created.
    this.addTracks(peer, entry);
    await entry.pc.setLocalDescription();
    const answer = entry.pc.localDescription;
    if (answer && this.isLive(peer, entry))
      await this.options.send(
        peer,
        answer.type as RtcSignalType,
        descriptionPayload(answer)
      );
  }

  /** Holds a candidate from a human not yet met, bounded per sender and by the call's human cap. */
  private holdEarly(peer: string, candidate: unknown): void {
    const k = peer.toLowerCase();
    const list = this.early.get(k);
    if (list) {
      if (list.length < MAX_EARLY_CANDIDATES) list.push(candidate);
      return;
    }
    if (this.early.size >= (this.config?.maxAudioHumans ?? 0)) return;
    this.early.set(k, [candidate]);
  }

  /** Applies the candidates that arrived before the remote description; a stale one is skipped, not fatal. */
  private async flushPending(entry: PeerEntry): Promise<void> {
    const queued = entry.pending.splice(0);
    for (const candidate of queued)
      await entry.pc.addIceCandidate(candidate).catch(() => undefined);
  }

  /**
   * The offering side reports the pair's outcome once. `connected` can come before getStats lists the
   * nominated pair, so an unresolved type is re-read on a short backoff and never reported as `failed`;
   * `reported` is set only when the POST comes back `sent`, so a failed, refused or throttled POST is
   * retried on the next state change.
   */
  private async reportIce(peer: string, entry: PeerEntry): Promise<void> {
    if (!entry.offered || entry.reported || entry.reporting) return;
    const state = entry.pc.iceConnectionState;
    if (state !== 'failed' && state !== 'connected' && state !== 'completed')
      return;
    entry.reporting = true;
    try {
      const outcome =
        state === 'failed' ? 'failed' : await this.resolvedType(peer, entry);
      if (!outcome) return;
      entry.reported =
        (await this.options.reportOutcome(peer, outcome)) === 'sent';
    } catch {
      // Left unreported: the next ICE state change tries again.
    } finally {
      entry.reporting = false;
    }
  }

  /** The nominated pair's candidate type, re-read while it is not listed yet; undefined if it never is. */
  private async resolvedType(
    peer: string,
    entry: PeerEntry
  ): Promise<RtcIceOutcome | undefined> {
    const waits = this.options.iceStatsRetryMs ?? ICE_STATS_RETRY_MS;
    for (let attempt = 0; ; attempt++) {
      if (this.peers.get(peer) !== entry) return undefined;
      if (entry.pc.iceConnectionState === 'failed') return 'failed';
      const type = await selectedCandidateType(entry.pc);
      if (type !== 'failed') return type;
      if (attempt >= waits.length) return undefined;
      await new Promise((r) => setTimeout(r, waits[attempt]));
    }
  }

  /** Swap the outgoing video on every peer; a peer with no video sender yet gets one (a renegotiation). */
  private async replaceVideo(track: RtcTrackLike | null): Promise<void> {
    if (!this.videoAllowed || !this.local) return;
    const stream = this.local.stream;
    await Promise.all(
      [...this.peers.values()].map(async (entry) => {
        if (entry.video) await entry.video.replaceTrack(track);
        else if (track && entry.tracksAdded)
          entry.video = entry.pc.addTrack(track, stream);
      })
    );
  }

  private clock(): number {
    return (this.options.now ?? Date.now)();
  }

  /**
   * Our first offer (offerSentAt is set only until the pair's first remote description) has waited past STALE_OFFER_MS with no answer: it went to a human who was not there, so their own
   * offer is taken (an implicit rollback of ours) rather than ignored, or the pair would never connect (S6c-1).
   */
  private staleOffer(entry: PeerEntry): boolean {
    return (
      !entry.makingOffer &&
      entry.pc.signalingState === 'have-local-offer' &&
      entry.offerSentAt !== null &&
      this.clock() - entry.offerSentAt >= STALE_OFFER_MS
    );
  }

  /**
   * Keeps the latest offer ignored during the pair's first negotiation and re-runs it through the peer's chain once
   * our own offer is stale. A retry that is still too early holds it again, so a timer firing a little early is safe.
   */
  private holdOffer(
    peer: string,
    entry: PeerEntry,
    description: RtcDescriptionLike
  ): void {
    entry.heldOffer = description;
    if (entry.cancelRetry || entry.offerSentAt === null) return;
    const wait = Math.max(1, entry.offerSentAt + STALE_OFFER_MS - this.clock());
    const setTimer = this.options.setTimer ?? defaultSetTimer;
    entry.cancelRetry = setTimer(() => {
      entry.cancelRetry = null;
      void this.enqueue(peer, async () => {
        const held = entry.heldOffer;
        entry.heldOffer = null;
        if (held && !entry.remoteSet && this.isLive(peer, entry))
          await this.describe(peer, entry, held);
      }).catch(() => undefined);
    }, wait);
  }

  private clearHeld(entry: PeerEntry): void {
    entry.heldOffer = null;
    entry.cancelRetry?.();
    entry.cancelRetry = null;
  }

  /** Whether `entry` is still this peer's connection: a hangup or drop during an await retires it. */
  private isLive(peer: string, entry: PeerEntry): boolean {
    return !!this.config && this.peers.get(peer) === entry;
  }

  private drop(peer: string, notify: boolean): void {
    const entry = this.peers.get(peer);
    if (!entry) return;
    this.peers.delete(peer);
    this.clearHeld(entry);
    entry.pc.close();
    if (notify) this.options.onPeerLeft?.(peer);
  }
}
